import { useCallback, useMemo, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { popOnboardingConfetti } from '@confluence/onboarding-helpers/entry-points/popOnboardingConfetti';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';

import { onboardingService } from './services/whiteboard-onboarding-service';

interface OnboardingServiceOptions {
	isNewWhiteboard?: boolean;
}

// TODO: Consider a better way to handle spotlight skipping so we don't have to export onboardingService
// `onboardingService` is shared with <WhiteboardOnboardingModalManager /> to allow for skipping spotlight onboarding if modal was skipped
// This means for now we have to use the global 'onboardingService' so that both our bridge API and the onboarding modal can share the state
export const useOnboardingService = ({
	isNewWhiteboard = false,
}: OnboardingServiceOptions = {}) => {
	const { isWhiteboardFeatureEnabled, isWhiteboardGAOnboardingEnabled } =
		useIsWhiteboardFeatureEnabled();

	const isGAOnboardingEnabled = isWhiteboardGAOnboardingEnabled();

	const isWhiteboardBetaOnboardingModalEnabled = isWhiteboardFeatureEnabled('onboardingModal');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [hasStartedWhiteboardBetaOnboardingModal] = useSkippableCoordination(
		'cc-whiteboards-onboarding-modal',
		!isWhiteboardBetaOnboardingModalEnabled || isGAOnboardingEnabled,
	);

	const shouldSkipBetaOnboardingSpotlight =
		!isWhiteboardBetaOnboardingModalEnabled ||
		isGAOnboardingEnabled ||
		onboardingService.onboardingSkipped ||
		hasStartedWhiteboardBetaOnboardingModal;

	const [hasStartedWhiteboardBetaOnboardingSpotlight, stopWhiteboardBetaOnboardingSpotlight] =
		useSkippableCoordination(
			'cc-whiteboards-onboarding-spotlight',
			shouldSkipBetaOnboardingSpotlight,
		);

	const [hasStartedWhiteboardGAOnboardingSpotlights, stopWhiteboardGAOnboardingSpotlights] =
		useSkippableCoordination('cc-whiteboards-ga-onboarding-spotlight', !isGAOnboardingEnabled);

	const handleOnboardingChange = useCallback((screen: string) => {
		onboardingService.previousOnboardingScreen = onboardingService.currentOnboardingScreen;
		onboardingService.currentOnboardingScreen = screen;

		if (onboardingService.previousOnboardingScreen === 'spotlight-sticky-viewer') {
			void popOnboardingConfetti();
		}
	}, []);

	const handleOnboardingEnd = useCallback(
		(screen: string) => {
			void stopWhiteboardBetaOnboardingSpotlight();
			void stopWhiteboardGAOnboardingSpotlights();

			if (screen === 'spotlight-sticky-creator') {
				void popOnboardingConfetti();
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'onboardingSpotlight',
					action: 'closed',
					actionSubject: 'onboardingSpotlight',
					attributes: {
						spotlight: screen,
					},
				},
			}).fire();
		},
		[
			stopWhiteboardBetaOnboardingSpotlight,
			stopWhiteboardGAOnboardingSpotlights,
			createAnalyticsEvent,
		],
	);

	useEffect(() => {
		onboardingService.registerOnOnboardingChangeCallback(handleOnboardingChange);
	}, [handleOnboardingChange]);

	useMemo(() => {
		onboardingService.registerOnOnboardingEndCallback(handleOnboardingEnd);
	}, [handleOnboardingEnd]);

	useMemo(() => {
		onboardingService.hasStartedWhiteboardOnboardingSpotlight =
			hasStartedWhiteboardBetaOnboardingSpotlight;
	}, [hasStartedWhiteboardBetaOnboardingSpotlight]);

	useEffect(() => {
		onboardingService.isEligibleForGAOnboarding =
			isGAOnboardingEnabled && hasStartedWhiteboardGAOnboardingSpotlights;
	}, [hasStartedWhiteboardGAOnboardingSpotlights, isGAOnboardingEnabled]);

	useEffect(() => {
		onboardingService.isNewWhiteboard = isNewWhiteboard;
	}, [isNewWhiteboard]);

	useEffect(() => {
		return () => {
			onboardingService.dispose();
		};
	}, []);

	return onboardingService;
};
